import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { SavedSignature, SignatureAssetType } from '@/components/pdf/types';
import { toast } from 'sonner';

// Utility function to convert DataURL to Blob
// More robust than fetch() for base64 data URLs
const dataUrlToBlob = (dataUrl: string): Blob => {
  const [header, base64] = dataUrl.split(',');
  const mimeMatch = header.match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/png';
  
  const binaryString = window.atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
};

export function useSavedSignatures() {
  const [signatures, setSignatures] = useState<SavedSignature[]>([]);
  const [stamps, setStamps] = useState<SavedSignature[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSignatures = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('user_signatures')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const mapped: SavedSignature[] = await Promise.all(
        (data || []).map(async (item) => {
          // Get signed URL for private bucket
          const { data: urlData } = await supabase.storage
            .from('user-assets')
            .createSignedUrl(item.file_path, 3600); // 1 hour

          return {
            id: item.id,
            type: item.type as SignatureAssetType,
            name: item.name,
            url: urlData?.signedUrl || '',
            filePath: item.file_path,
            isDefault: item.is_default || false,
            createdAt: item.created_at,
          };
        })
      );

      setSignatures(mapped.filter(s => s.type === 'signature' || s.type === 'initials'));
      setStamps(mapped.filter(s => s.type === 'stamp'));
    } catch (error) {
      console.error('Error fetching signatures:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSignatures();
  }, [fetchSignatures]);

  const saveSignature = useCallback(async (
    dataUrl: string,
    type: SignatureAssetType,
    name: string
  ): Promise<SavedSignature | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Non authentifié');

      // Convert data URL to blob using robust method
      const blob = dataUrlToBlob(dataUrl);

      // Upload to storage
      const fileName = `${crypto.randomUUID()}.png`;
      const filePath = `${user.id}/signatures/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('user-assets')
        .upload(filePath, blob, { contentType: 'image/png' });

      if (uploadError) throw uploadError;

      // Create database record
      const { data, error: insertError } = await supabase
        .from('user_signatures')
        .insert({
          user_id: user.id,
          type,
          name,
          file_path: filePath,
          is_default: false,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Get signed URL
      const { data: urlData } = await supabase.storage
        .from('user-assets')
        .createSignedUrl(filePath, 3600);

      const newSignature: SavedSignature = {
        id: data.id,
        type,
        name,
        url: urlData?.signedUrl || dataUrl,
        filePath,
        isDefault: false,
        createdAt: data.created_at,
      };

      // Update local state
      if (type === 'stamp') {
        setStamps(prev => [newSignature, ...prev]);
      } else {
        setSignatures(prev => [newSignature, ...prev]);
      }

      toast.success(`${type === 'stamp' ? 'Tampon' : 'Signature'} sauvegardé(e)`);
      return newSignature;
    } catch (error) {
      console.error('Error saving signature:', error);
      toast.error('Erreur lors de la sauvegarde');
      return null;
    }
  }, []);

  const deleteSignature = useCallback(async (id: string, filePath: string) => {
    try {
      // Delete from storage
      await supabase.storage.from('user-assets').remove([filePath]);

      // Delete from database
      const { error } = await supabase
        .from('user_signatures')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Update local state
      setSignatures(prev => prev.filter(s => s.id !== id));
      setStamps(prev => prev.filter(s => s.id !== id));

      toast.success('Supprimé');
    } catch (error) {
      console.error('Error deleting signature:', error);
      toast.error('Erreur lors de la suppression');
    }
  }, []);

  const setAsDefault = useCallback(async (id: string, type: SignatureAssetType) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Remove default from all of same type
      await supabase
        .from('user_signatures')
        .update({ is_default: false })
        .eq('user_id', user.id)
        .eq('type', type);

      // Set new default
      await supabase
        .from('user_signatures')
        .update({ is_default: true })
        .eq('id', id);

      // Update local state
      const updateList = (list: SavedSignature[]) =>
        list.map(s => ({
          ...s,
          isDefault: s.id === id,
        }));

      if (type === 'stamp') {
        setStamps(updateList);
      } else {
        setSignatures(updateList);
      }

      toast.success('Défini par défaut');
    } catch (error) {
      console.error('Error setting default:', error);
    }
  }, []);

  return {
    signatures,
    stamps,
    loading,
    saveSignature,
    deleteSignature,
    setAsDefault,
    refresh: fetchSignatures,
  };
}
